/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Admin\GestionUtilisateurs.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - GESTION DES UTILISATEURS ADMIN

📋 DESCRIPTION:
Interface d'administration pour la gestion complète des utilisateurs.
CRUD complet avec interface moderne et sécurisée.

🎯 RESPONSABILITÉS:
- Affichage de la liste des utilisateurs avec filtres
- Création de nouveaux utilisateurs avec validation
- Modification des profils et rôles existants
- Suppression sécurisée avec confirmations
- Gestion des permissions et rôles

🔗 DÉPENDANCES:
- AdminService: API pour les opérations CRUD
- React: Hooks useState, useEffect pour la gestion d'état
- Tailwind CSS: Styles et composants UI

📡 INTERACTIONS:
- Backend: Routes /api/admin/users/*
- AdminService: Interface de communication
- Notifications: Feedback utilisateur
- Modals: Formulaires de création/modification

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Interface responsive avec Tailwind CSS
- Validation côté client et serveur
- Gestion d'erreurs avec messages explicites
- Protection contre la suppression du dernier admin
*/

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminService, AdminUser, CreateUserData, UpdateUserData } from '../../services/AdminService';

type SortableColumn =
  | 'name'
  | 'email'
  | 'phone'
  | 'companyName'
  | 'role'
  | 'status'
  | 'subscriptionType'
  | 'createdAt'
  | 'lastLoginAt'
  | 'lastActivityAt'
  | 'devisCountTotal'
  | 'articlesCountTotal'
  | 'clientsCountTotal'
  | 'loginCountTotal';

const DEFAULT_SORT_DIRECTION: Record<SortableColumn, 'asc' | 'desc'> = {
  name: 'asc',
  email: 'asc',
  phone: 'asc',
  companyName: 'asc',
  role: 'asc',
  status: 'asc',
  subscriptionType: 'asc',
  createdAt: 'desc',
  lastLoginAt: 'desc',
  lastActivityAt: 'desc',
  devisCountTotal: 'desc',
  articlesCountTotal: 'desc',
  clientsCountTotal: 'desc',
  loginCountTotal: 'desc',
};

interface GestionUtilisateursProps {}

const createFormInitialState = (): CreateUserData => ({
  email: '',
  name: '',
  role: 'user',
  companyName: '',
  phone: '',
  is_beta: false,
});

const GestionUtilisateurs: React.FC<GestionUtilisateursProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // États pour la gestion des utilisateurs
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // États pour les modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // États pour les filtres
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'manager' | 'user'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'trial' | 'past_due'>('all');
  const [filterBetaOnly, setFilterBetaOnly] = useState(false);
  const [filterConfirmedOnly, setFilterConfirmedOnly] = useState(false);
  const [filterTrialOnly, setFilterTrialOnly] = useState(false);
  const [seedLoadingUser, setSeedLoadingUser] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<SortableColumn | undefined>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // États pour les formulaires
  const [createForm, setCreateForm] = useState<CreateUserData>(createFormInitialState());
  const [createSubscriptionType, setCreateSubscriptionType] = useState<'standard' | 'premium'>('standard');
  const [createSubscriptionEndDate, setCreateSubscriptionEndDate] = useState<string>('');

  const [editForm, setEditForm] = useState<UpdateUserData>({
    name: '',
    role: 'user',
    companyName: '',
    phone: '',
    is_beta: false,
  });
  const [editSubscriptionType, setEditSubscriptionType] = useState<'standard' | 'premium'>('standard');
  const [editSubscriptionEndDate, setEditSubscriptionEndDate] = useState<string>('');

  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const formatDateInput = (value: any) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  };

  const handleSort = (column: SortableColumn) => {
    const isSameColumn = sortBy === column;
    const nextDir = isSameColumn ? (sortDir === 'asc' ? 'desc' : 'asc') : DEFAULT_SORT_DIRECTION[column];
    setSortBy(column);
    setSortDir(nextDir);
    setPage(1);
  };

  const renderSortIcon = (column: SortableColumn) => {
    if (sortBy !== column) {
      return <span className="text-gray-300">↕</span>;
    }
    return <span className="text-gray-700">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const renderHeaderCell = (
    label: string,
    column: SortableColumn,
    className = '',
    align: 'left' | 'right' = 'left'
  ) => (
    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}>
      <button
        type="button"
        onClick={() => handleSort(column)}
        className={`w-full flex items-center gap-1 ${
          align === 'right' ? 'justify-end text-right' : 'justify-start text-left'
        }`}
      >
        <span>{label}</span>
        {renderSortIcon(column)}
      </button>
    </th>
  );

  // Fonction pour charger les utilisateurs
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getUsers({
        search: searchTerm.trim() || undefined,
        role: filterRole !== 'all' ? filterRole : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        page,
        pageSize,
        sortBy: sortBy || undefined,
        sortDir,
      });
      setUsers(response.items);
      setTotal(response.total);
      setTotalPages(response.totalPages);
      if (response.sort) {
        setSortBy((response.sort.sortBy as SortableColumn | undefined) || undefined);
        if (response.sort.sortDir) {
          setSortDir(response.sort.sortDir);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  // Charger les utilisateurs au montage du composant et lors des changements de filtres
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole, filterStatus, searchTerm, page, pageSize, sortBy, sortDir]);

  const resetCreateForm = () => {
    setCreateForm(createFormInitialState());
    setCreateSubscriptionType('standard');
    setCreateSubscriptionEndDate('');
  };

  const openCreateModal = () => {
    setError(null);
    setSuccess(null);
    resetCreateForm();
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  // Fonction pour créer un utilisateur
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.companyName || !createForm.companyName.trim()) {
      setError("Le champ Entreprise est requis");
      return;
    }
    try {
      setError(null);
      const response = await adminService.createUser({
        ...createForm,
        companyName: createForm.companyName.trim(),
        phone: (createForm.phone || '').trim() || null,
        subscription_type: createSubscriptionType,
        subscription_end_date: createSubscriptionEndDate || null,
      });
      const invitationSent = (response as any)?.invitation?.sent;
      const expiresIn = (response as any)?.invitation?.expiresInMinutes;
      const warnings = ((response as any)?.warnings as string[]) || [];
      const baseMessage = invitationSent
        ? `Utilisateur créé, invitation envoyée (expiration ${expiresIn ?? '?'} min)`
        : 'Utilisateur créé (invitation non envoyée)';
      const warningText = warnings.length ? ` Avertissement: ${warnings.join(' | ')}` : '';
      setSuccess(`${baseMessage}${warningText}`);
      setShowCreateModal(false);
      resetCreateForm();
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création de l\'utilisateur');
    }
  };

  // Fonction pour modifier un utilisateur
  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setError(null);
      await adminService.updateUser(selectedUser.email, {
        ...editForm,
        phone: (editForm.phone || '').trim() || null,
        subscription_type: editSubscriptionType,
        subscription_end_date: editSubscriptionEndDate || null,
      });
      setSuccess('Utilisateur modifié avec succès');
      setShowEditModal(false);
      setSelectedUser(null);
      setEditSubscriptionEndDate('');
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification de l\'utilisateur');
    }
  };

  // Fonction pour ouvrir le modal d'édition
  const openEditModal = (user: AdminUser) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      role: user.role,
      companyName: user.companyName || '',
      phone: (user as any).phone || '',
      is_beta: (user as any).isBeta ?? (user as any).is_beta ?? false,
    });
    setEditSubscriptionType(((user as any).subscriptionType ?? (user as any).subscription_type) || 'standard');
    const endDate = (user as any).subscriptionEndDate ?? (user as any).subscription_end_date;
    setEditSubscriptionEndDate(formatDateInput(endDate));
    setShowEditModal(true);
  };

  useEffect(() => {
    const state = location.state as any;
    if (state?.editUserEmail && users.length > 0) {
      const target = users.find((u) => u.email === state.editUserEmail);
      if (target) {
        openEditModal(target);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, users]);

  // Fonction pour ouvrir le modal de réinitialisation de mot de passe
  const openResetPasswordModal = (user: AdminUser) => {
    setSelectedUser(user);
    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    setShowResetPasswordModal(true);
  };

  // Rejouer le seed defaults pour un utilisateur
  const handleSeedDefaults = async (user: AdminUser) => {
    try {
      setError(null);
      setSeedLoadingUser(user.userId);
      await adminService.seedDefaults(user.userId);
      setSuccess(`Seed rejoue pour ${user.name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du seed defaults');
    } finally {
      setSeedLoadingUser(null);
    }
  };

  // Lancer un trial de 7 jours pour un utilisateur
  const startTrialForUser = async (user: AdminUser) => {
    try {
      setError(null);
      setSuccess(null);
      const now = new Date();
      const inSevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      await adminService.updateUser(user.email, {
        trial_started_at: now.toISOString(),
        trial_end_at: inSevenDays.toISOString(),
        trial_used: true,
        subscription_status: (user as any).subscription_status || (user as any).subscriptionStatus || null,
      });
      setSuccess(`Periode d'essai lancee pour ${user.name} jusqu'au ${inSevenDays.toLocaleDateString()}`);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du lancement du trial');
    }
  };

  // Activer/désactiver un utilisateur (soft)
  const toggleActiveUser = async (user: AdminUser, active: boolean) => {
    // Empêcher de désactiver le dernier admin actif
    const activeAdmins = users.filter(
      (u) => u.role === 'admin' && isUserActive(u)
    ).length;
    if (!active && user.role === 'admin' && activeAdmins <= 1) {
      setError('Impossible de desactiver le dernier administrateur actif');
      return;
    }
    try {
      setError(null);
      await adminService.updateUser(user.email, { is_active: active });
      setSuccess(active ? `${user.name} reactive` : `${user.name} desactive`);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du changement de statut');
    }
  };

  // Fonction pour réinitialiser le mot de passe
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    // Validation: les mots de passe doivent correspondre
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    // Validation: longueur minimale
    if (resetPasswordForm.newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      setError(null);
      await adminService.updateUser(selectedUser.email, {
        password: resetPasswordForm.newPassword
      });
      setSuccess(`Mot de passe réinitialisé avec succès pour ${selectedUser.name}`);
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la réinitialisation du mot de passe');
    }
  };

  // Statut admin consolidé
  const getUserStatus = (user: AdminUser) => {
    const explicitStatus = (user.status || '').toLowerCase();
    if (explicitStatus) return explicitStatus;
    const isActive = (user as any).isActive ?? (user as any).is_active ?? true;
    return isActive ? 'active' : 'inactive';
  };

  const isUserActive = (user: AdminUser) => (user as any).isActive ?? (user as any).is_active ?? true;

  // Filtrer les utilisateurs (filtres complémentaires côté client)
  const filteredUsers = users.filter(user => {
    const status = getUserStatus(user);
    const isBeta = (user as any).isBeta ?? (user as any).is_beta ?? false;
    const isConfirmed = (user as any).isConfirmed ?? (user as any).is_confirmed ?? false;

    if (filterBetaOnly && !isBeta) return false;
    if (filterConfirmedOnly && !isConfirmed) return false;
    if (filterTrialOnly && status !== 'trial') return false;

    return true;
  });

  // Fonction pour obtenir la couleur du badge de rôle
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'manager':
        return 'bg-yellow-100 text-yellow-800';
      case 'user':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour obtenir le libellé du rôle
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrateur plateforme';
      case 'manager':
        return 'Manager';
      case 'user':
        return 'Utilisateur';
      default:
        return role;
    }
  };

  const getStatusBadge = (user: AdminUser) => {
    const status = getUserStatus(user);
    switch (status) {
      case 'trial':
        return { label: 'Trial', classes: 'bg-amber-100 text-amber-800' };
      case 'past_due':
      case 'expired':
        return { label: 'Expiré', classes: 'bg-red-100 text-red-800' };
      case 'inactive':
      case 'blocked':
        return { label: 'Inactif', classes: 'bg-gray-100 text-gray-700' };
      case 'active':
      default:
        return { label: 'Actif', classes: 'bg-green-100 text-green-800' };
    }
  };

  const formatDateDisplay = (value?: string | null, withTime = false) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleString('fr-FR', withTime ? {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    } : {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getPlanLabel = (user: AdminUser) => {
    const type = (user.subscriptionType || (user as any).subscription_type || 'standard') === 'premium' ? 'Premium' : 'Standard';
    const status = user.subscriptionStatus || (user as any).subscription_status;
    const endDate = user.subscriptionEndDate || (user as any).subscription_end_date;
    const end = endDate ? formatDateDisplay(endDate) : null;
    const parts = [type];
    if (status) parts.push(status);
    if (end) parts.push(`fin ${end}`);
    return parts.join(' - ') || '--';
  };

  // Effacer les messages après 5 secondes
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="p-4 lg:p-6 w-full max-w-none">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          🛠️ Gestion des Utilisateurs
        </h1>
        <p className="text-gray-600">
          Gérez les comptes utilisateurs, leurs rôles et permissions.
        </p>
      </div>

      {/* Messages de feedback */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">❌</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-green-400">✅</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Zone filtres desktop */}
      <div className="mb-6 bg-white border rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="text-sm text-gray-700 font-medium">Recherche</label>
            <div className="relative mt-1">
              <input
                type="text"
                placeholder="Nom, email, entreprise..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400">🔍</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-700 font-medium">Rôle</label>
            <select
              value={filterRole}
              onChange={(e) => {
                setFilterRole(e.target.value as 'all' | 'admin' | 'manager' | 'user');
                setPage(1);
              }}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les rôles</option>
              <option value="admin">Administrateurs</option>
              <option value="manager">Managers</option>
              <option value="user">Utilisateurs</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-700 font-medium">Statut</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value as 'all' | 'active' | 'inactive' | 'trial' | 'past_due');
                setPage(1);
              }}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="trial">Trial</option>
              <option value="past_due">Expiré / à régulariser</option>
              <option value="inactive">Inactif / bloqué</option>
            </select>
          </div>

          <div className="flex justify-end">
            <button
              onClick={openCreateModal}
              className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              ➕ Nouvel utilisateur
            </button>
          </div>

          <div className="md:col-span-3 lg:col-span-4 flex flex-wrap gap-4 items-center justify-between pt-2">
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filterBetaOnly}
                  onChange={(e) => {
                    setFilterBetaOnly(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                Beta
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filterConfirmedOnly}
                  onChange={(e) => {
                    setFilterConfirmedOnly(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                Comptes confirmés
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filterTrialOnly}
                  onChange={(e) => {
                    setFilterTrialOnly(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                Trial en cours
              </label>
            </div>
            <div className="text-sm text-gray-600 font-medium">
              {total} utilisateurs
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des utilisateurs */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Chargement...</span>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {renderHeaderCell('Utilisateur', 'name', 'w-48')}
                  {renderHeaderCell('Email', 'email', 'w-52')}
                  {renderHeaderCell('Telephone', 'phone', 'w-32')}
                  {renderHeaderCell('Entreprise', 'companyName', 'w-40')}
                  {renderHeaderCell('Role', 'role', 'w-32')}
                  {renderHeaderCell('Statut', 'status', 'w-28')}
                  {renderHeaderCell('Plan / abonnement', 'subscriptionType', 'w-44')}
                  {renderHeaderCell('Devis', 'devisCountTotal', 'w-24 text-right', 'right')}
                  {renderHeaderCell('Articles', 'articlesCountTotal', 'w-24 text-right', 'right')}
                  {renderHeaderCell('Clients', 'clientsCountTotal', 'w-24 text-right', 'right')}
                  {renderHeaderCell('Connexions', 'loginCountTotal', 'w-28 text-right', 'right')}
                  {renderHeaderCell('Cree le', 'createdAt', 'w-32')}
                  {renderHeaderCell('Derniere connexion', 'lastLoginAt', 'w-40')}
                  {renderHeaderCell('Derniere activite', 'lastActivityAt', 'w-40')}
                  <th className="w-56 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const statusBadge = getStatusBadge(user);
                  const active = isUserActive(user);
                  return (
                    <tr key={user.email} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium text-sm">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                            <div className="text-xs text-gray-500">ID: {user.userId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 break-words">{user.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{user.phone || '--'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 break-words">{user.companyName || '--'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge.classes}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 break-words">{getPlanLabel(user)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{user.devisCountTotal ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{user.articlesCountTotal ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{user.clientsCountTotal ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{user.loginCountTotal ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDateDisplay(user.createdAt || (user as any).created_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDateDisplay(user.lastLoginAt || (user as any).last_login_at, true)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDateDisplay(user.lastActivityAt || (user as any).last_activity_at, true)}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="text-gray-700 hover:text-gray-900"
                            title="Voir la fiche utilisateur"
                          >
                            Voir
                          </button>
                          <button
                            onClick={() => openEditModal(user)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Modifier l'utilisateur"
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            onClick={() => startTrialForUser(user)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Lancer une période d'essai de 7 jours"
                          >
                            Trial 7j
                          </button>
                          <button
                            onClick={() => handleSeedDefaults(user)}
                            className="text-green-600 hover:text-green-900 disabled:text-gray-400"
                            title="Rejouer le seed defaults"
                            disabled={seedLoadingUser === user.userId}
                          >
                            {seedLoadingUser === user.userId ? 'Seed...' : 'Rejouer seed'}
                          </button>
                          {active ? (
                            <button
                              onClick={() => toggleActiveUser(user, false)}
                              className="text-red-600 hover:text-red-900"
                              title="Désactiver l'utilisateur"
                            >
                              ⛔ Désactiver
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleActiveUser(user, true)}
                              className="text-green-600 hover:text-green-900"
                              title="Réactiver l'utilisateur"
                            >
                              ✔ Réactiver
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <span className="text-4xl mb-4 block">🔍</span>
              <p className="text-gray-500">Aucun utilisateur trouvé</p>
            </div>
          )}

          {filteredUsers.length > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t">
              <div className="text-sm text-gray-700">
                Page {page} / {totalPages} - {total} resultats
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="px-3 py-1 rounded-md border border-gray-300 text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="px-3 py-1 rounded-md border border-gray-300 text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Modal de création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Créer un nouvel utilisateur
              </h3>
              <form onSubmit={handleCreateUser} autoComplete="off">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={createForm.email}
                    name="new-user-email"
                    autoComplete="off"
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    required
                    value={createForm.name}
                    name="new-user-name"
                    autoComplete="off"
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="mb-4 text-sm text-gray-600">
                  Un email d'activation sera envoyé à l'utilisateur pour définir son mot de passe.
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rôle
                  </label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone (optionnel)
                  </label>
                  <input
                    type="tel"
                    value={createForm.phone || ''}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+33 ..."
                  />
                </div>
                <div className="mb-4 grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type d'abonnement
                    </label>
                    <select
                      value={createSubscriptionType}
                      onChange={(e) => setCreateSubscriptionType(e.target.value as 'standard' | 'premium')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fin d'abonnement (optionnel)
                    </label>
                    <input
                      type="date"
                      value={createSubscriptionEndDate}
                      onChange={(e) => setCreateSubscriptionEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mb-4 flex items-start space-x-3">
                  <input
                    id="create-is-beta"
                    type="checkbox"
                    checked={!!createForm.is_beta}
                    onChange={(e) => setCreateForm({ ...createForm, is_beta: e.target.checked })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="create-is-beta" className="text-sm text-gray-700">
                    <span className="font-medium">Beta-testeur</span>
                    <br />
                    <span className="text-xs text-gray-500">
                      Programme bêta : utilisateurs prêts à tester et à partager leurs retours.
                    </span>
                  </label>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise *
                  </label>
                  <input
                    type="text"
                    required
                    value={createForm.companyName}
                    onChange={(e) => setCreateForm({ ...createForm, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Créer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Modifier l'utilisateur
              </h3>
              <form onSubmit={handleEditUser}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email (non modifiable)
                  </label>
                  <input
                    type="email"
                    value={selectedUser.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rôle
                  </label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
                <div className="mb-4 grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type d'abonnement
                    </label>
                    <select
                      value={editSubscriptionType}
                      onChange={(e) => setEditSubscriptionType(e.target.value as 'standard' | 'premium')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fin d'abonnement (optionnel)
                    </label>
                    <input
                      type="date"
                      value={editSubscriptionEndDate}
                      onChange={(e) => setEditSubscriptionEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mb-4 flex items-start space-x-3">
                  <input
                    id="edit-is-beta"
                    type="checkbox"
                    checked={!!editForm.is_beta}
                    onChange={(e) => setEditForm({ ...editForm, is_beta: e.target.checked })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="edit-is-beta" className="text-sm text-gray-700">
                    <span className="font-medium">Beta-testeur</span>
                    <br />
                    <span className="text-xs text-gray-500">
                      Programme bêta : utilisateurs prêts à tester activement et à partager leurs retours.
                    </span>
                  </label>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise
                  </label>
                  <input
                    type="text"
                    value={editForm.companyName}
                    onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Modifier
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de réinitialisation de mot de passe */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                  <span className="text-orange-600 text-xl">🔐</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  Réinitialiser le mot de passe
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Utilisateur : <strong>{selectedUser.name}</strong> ({selectedUser.email})
              </p>
              <form onSubmit={handleResetPassword}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nouveau mot de passe *
                  </label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={resetPasswordForm.newPassword}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Minimum 6 caractères"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe *
                  </label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={resetPasswordForm.confirmPassword}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Retapez le mot de passe"
                  />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-yellow-800">
                    ⚠️ L'utilisateur devra utiliser ce nouveau mot de passe lors de sa prochaine connexion.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                  >
                    Réinitialiser
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionUtilisateurs;
